import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Input, Label, Row, Col, Progress } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { leePrepedidos } from "./importaPrepedido";

// Sube el archivo de prepedidos del cliente (la "BASE DE DATOS") y lo deja
// capturado: un pedido por cada prepedido, con un renglon por color y talla.
// Antes de guardar nada se ve que va a pasar con cada prepedido y que modelos,
// colores o tallas se van a dar de alta.
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);

// Las columnas son las mismas del archivo que manda el cliente ("BASE DE DATOS",
// hoja PP), en el mismo orden, para que lo pueda llenar tal cual.
const COLUMNAS = [
  // "# PREPEDIDO" es opcional y solo sirve para juntar renglones en un mismo
  // pedido; el folio lo pone el sistema
  "# PREPEDIDO", "CLIENTE", "ORDEN DE COMPRA", "VENDEDOR", "PROVEEDOR",
  // REFERENCIA DE MUESTRA se quito del layout (nov-2026); si un archivo viejo la
  // trae, se sigue leyendo
  // MODELO es el codigo y PRENDA el nombre; los catalogos van igual que en el alta
  // de modelo del sistema: linea, marca, departamento, coleccion, composicion y
  // empresa (nov-2026: el cliente pidio que fueran los mismos campos)
  "MODELO", "PRENDA", "LINEA", "MARCA", "DEPARTAMENTO", "COLECCION", "COMPOSICION", "EMPRESA",
  "COMPRADORA",
  // Colores y tallas se capturan igual: la lista y su proporcion. Con eso y la
  // CANTIDAD final el sistema reparte solo (ya no van las columnas 28..42).
  // REF COLOR tambien se quito (nov-2026); un archivo viejo que la traiga se sigue leyendo
  "COLOR", "PROPORCION COLOR",
  "TALLA", "PROPORCION TALLA",
  // UN (unidad) tambien se quito (nov-2026)
  "CANTIDAD",
  "COSTO FOB", "COSTO DLLS", "COSTO CLIENTE", "COSTO MXN", "Precio Venta Publico",
  "ENTREGA INICIAL", "FECHA FOB", "CANCELACION",
  // MONARCH se quito (nov-2026); un archivo viejo que la traiga se sigue leyendo
  "COMENTARIO CLIENTE", "COMENTARIO INTERNO",
];
// Solo se exige lo minimo para poder capturar: de quien es y que modelo con cuantas piezas.
const OBLIGATORIAS = ["CLIENTE", "MODELO", "CANTIDAD"];
const EJEMPLO = {
  "# PREPEDIDO": "PP1", CLIENTE: "OPTIMA", "ORDEN DE COMPRA": "OC-8821", VENDEDOR: "PATY",
  // el MODELO del ejemplo lo marca como tal: ese renglon nunca se captura
  PROVEEDOR: "FANG", MODELO: "EJEMPLO-BORRAR", PRENDA: "TOP", LINEA: "BLUSAS", MARCA: "CAVANNA",
  DEPARTAMENTO: "DAMA", COLECCION: "OTONO 2026", COMPOSICION: "100% ALGODON", EMPRESA: "CAVANNA TEXTIL",
  COMPRADORA: "PATY", COLOR: "HUESO-NEGRO-ROSA",
  "PROPORCION COLOR": "2-1-1", TALLA: "CH-M-G-EG", "PROPORCION TALLA": "1-2-2-1",
  CANTIDAD: 1800, "COSTO FOB": 19.5, "COSTO DLLS": 82.89, "COSTO CLIENTE": 95,
  "Precio Venta Publico": 230,
};

function ImportarPrepedidoModal({ abierto, cerrar, onImportado }) {
  const [archivo, setArchivo] = useState("");
  // candado: lo que ya se capturo antes, para no subirlo dos veces
  const [yaCapturados, setYaCapturados] = useState({});
  const [prepedidos, setPrepedidos] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [elegidos, setElegidos] = useState({});
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    // mapa "PP|CLIENTE" -> pedido que ya lo capturo
    axios.get(process.env.REACT_APP_URL_PEDIDOS, { headers: headers() })
      .then((r) => {
        const mapa = {};
        (r.data || []).forEach((p) => {
          const cli = String(p.clientes?.[0]?.nombre_comercial || "").trim().toUpperCase();
          const oc = String(p.ordenCompraCliente || "").trim().toUpperCase();
          if (oc && oc !== "NA") mapa[`OC|${oc}|${cli}`] = { idPedido: p.idPedido, fecha: p.fecha };
          const pp = String(p.prepedidoCliente || "").trim().toUpperCase();
          if (pp && pp !== "NA") mapa[`PP|${pp}|${cli}`] = { idPedido: p.idPedido, fecha: p.fecha };
        });
        setYaCapturados(mapa);
      })
      .catch(() => setYaCapturados({}));
  }, [abierto]);
  const [verAvisos, setVerAvisos] = useState(false);

  function descargaLayout() {
    const wb = XLSX.utils.book_new();
    const encabezado = COLUMNAS.map((c) => (OBLIGATORIAS.includes(c) ? `${c} *` : c));
    const hoja = XLSX.utils.aoa_to_sheet([encabezado, COLUMNAS.map((c) => EJEMPLO[c] ?? "")]);
    hoja["!cols"] = COLUMNAS.map((c) => ({ wch: Math.max(12, c.length + 4) }));
    XLSX.utils.book_append_sheet(wb, hoja, "PP");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ["COMO LLENAR EL LAYOUT DE PREPEDIDOS"],
      [],
      ["1", "Un renglon por modelo, empezando en el renglon 3. El renglon 2 es un ejemplo: se puede borrar, y aunque se deje no se captura."],
      ["2", "# PREPEDIDO (opcional) junta renglones en un solo pedido: los que traen el mismo numero y el mismo CLIENTE quedan juntos. El folio del pedido lo pone el sistema."],
      ["3", "Si # PREPEDIDO va vacio, se juntan los renglones del mismo CLIENTE y la misma ORDEN DE COMPRA."],
      ["4", "MODELO es el codigo del articulo y PRENDA su nombre. LINEA, MARCA, DEPARTAMENTO, COLECCION, COMPOSICION, PROVEEDOR y EMPRESA son los mismos datos del alta de modelo del sistema: si no existen se dan de alta (la empresa debe existir)."],
      ["5", "Colores y tallas se capturan igual: la lista y su proporcion, y al final la CANTIDAD total. Nada mas."],
      ["6", "COLOR: los colores separados por guion, coma o uno por renglon (alt+enter). Ej: NEGRO-BLANCO-ROJO o NEGRO,BLANCO,ROJO."],
      ["7", "PROPORCION COLOR: cuanto lleva cada color, en el mismo orden. Ej: 2-1-1. Vacia = parejo."],
      ["8", "TALLA: la escala con guiones. Ej: CH-M-G-EG."],
      ["9", "PROPORCION TALLA: cuanto lleva cada talla, en el mismo orden. Ej: 1-2-2-1. Vacia = parejo."],
      ["10", "CANTIDAD: el total de piezas del renglon. El sistema lo reparte por talla y por color con las proporciones y siempre cuadra."],
      ["11", "Ejemplo: NEGRO-BLANCO-ROJO 2-1-1, CH-M-G-EG 1-2-2-1, CANTIDAD 1800 -> NEGRO 900, BLANCO 450, ROJO 450; CH 300, M 600, G 600, EG 300."],
      ["12", "Solo son obligatorias las columnas con *: CLIENTE, MODELO y CANTIDAD. Lo demas es opcional."],
      ["13", "Lo que se deje vacio queda en NA. Los modelos, colores y tallas que no existan se dan de alta solos."],
      ["14", "El mismo MODELO puede repetirse (cambia el dibujo): cada renglon se captura como articulo aparte."],
      ["15", "COSTO CLIENTE es el precio que se le cobra al cliente."],
      ["16", "Si un prepedido (o una ORDEN DE COMPRA, cuando no trae # PREPEDIDO) ya se subio, el sistema avisa antes de capturarlo otra vez."],
      ["17", "Si el CLIENTE no existe se da de alta solo, con su nombre; los demas datos del cliente se completan despues en el catalogo."],
    ]), "Instrucciones");
    XLSX.writeFile(wb, "LayoutPrepedidos.xlsx");
  }

  function leeArchivo(file) {
    if (!file) return;
    setArchivo(file.name); setPrepedidos([]); setAvisos([]);
    const lector = new FileReader();
    lector.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        // la hoja del cliente se llama PP; si no, se toma la primera
        const hoja = wb.Sheets["PP"] || wb.Sheets[wb.SheetNames[0]];
        const { prepedidos: pps, avisos: avs } = leePrepedidos(XLSX.utils.sheet_to_json(hoja, { defval: "" }));
        setPrepedidos(pps); setAvisos(avs);
        // los que ya se capturaron antes entran desmarcados
        setElegidos(Object.fromEntries(pps.map((p) => [p.numero + p.cliente, !yaImportado(p)])));
      } catch (err) {
        Swal.fire({ icon: "error", title: "No se pudo leer el archivo", text: "Revisa que sea el archivo de prepedidos (hoja PP)." });
      }
    };
    lector.readAsArrayBuffer(file);
  }

  const yaImportado = (p) => {
    const cli = String(p.cliente || "").trim().toUpperCase();
    // primero el # PREPEDIDO del archivo (varios pueden traer la misma OC);
    // si no trae, la orden de compra del cliente
    const pp = String(p.prepedidoArchivo || "").trim().toUpperCase();
    if (pp) return yaCapturados[`PP|${pp}|${cli}`];
    const oc = String(p.ordenCompraCliente || "").trim().toUpperCase();
    return oc ? yaCapturados[`OC|${oc}|${cli}`] : null;
  };

  const marcados = prepedidos.filter((p) => elegidos[p.numero + p.cliente]);
  const repetidosMarcados = marcados.filter((p) => yaImportado(p));
  // cada renglon es un dibujo: se da de alta un articulo por renglon, y si el
  // codigo ya esta ocupado se le pone sufijo (-2, -3...)
  const dibujosExtra = (() => {
    const vistos = new Set(); let n = 0;
    marcados.forEach((p) => p.articulos.forEach((a) => {
      const k = String(a.modelo).trim().toUpperCase();
      if (vistos.has(k)) n++; else vistos.add(k);
    }));
    return n;
  })();
  const piezas = marcados.reduce((s, p) => s + p.piezas, 0);
  const modelos = marcados.reduce((s, p) => s + p.articulos.length, 0);
  const renglones = marcados.reduce((s, p) => s + p.articulos.reduce((x, a) => x + a.celdas.length, 0), 0);

  async function importa() {
    if (!marcados.length) {
      Swal.fire({ icon: "warning", title: "Nada marcado", text: "Elige por lo menos un prepedido." });
      return;
    }
    const ok = await Swal.fire({
      icon: "question",
      title: `Se van a capturar ${marcados.length} prepedido(s)`,
      html: `${num(modelos)} modelos · ${num(renglones)} renglones por color y talla · <b>${num(piezas)} piezas</b>.<br><br>
             Los modelos, colores y tallas que no existan se dan de alta.` +
            `<br><b style="color:#b45309">Cada renglon da de alta su propio modelo (un dibujo = un articulo); los codigos ocupados llevan sufijo.</b>`,
      showCancelButton: true, confirmButtonText: "Sí, capturar", cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    // el que ya se habia capturado necesita un si extra
    if (repetidosMarcados.length) {
      const otra = await Swal.fire({
        icon: "warning",
        title: `${repetidosMarcados.length} prepedido(s) ya se habían importado`,
        html: repetidosMarcados.map((p) => {
          const y = yaImportado(p);
          return `<b>${p.numero}</b> · ${p.cliente} → pedido #${y.idPedido} del ${y.fecha}`;
        }).join("<br>") + "<br><br>Si sigues, se vuelven a capturar <b>duplicados</b>.",
        showCancelButton: true, confirmButtonText: "Sí, capturar de nuevo", cancelButtonText: "Mejor no",
      });
      if (!otra.isConfirmed) return;
    }
    setGuardando(true);
    Swal.fire({ title: "Capturando", html: "Puede tardar un par de minutos.", allowOutsideClick: false, onBeforeOpen: () => Swal.showLoading() });
    try {
      const user = JSON.parse(atob((localStorage.getItem("app_token") || "..").split(".")[1] || "e30=")).id;
      const aMandar = marcados.map((p) => (yaImportado(p) ? { ...p, forzar: true } : p));
      const r = await axios.post(`${process.env.REACT_APP_URL_PEDIDOS}/importar`, { user, prepedidos: aMandar }, { headers: headers(), timeout: 600000 });
      setGuardando(false);
      Swal.close();
      const d = r.data;
      await Swal.fire({
        icon: "success",
        title: `${d.pedidos.length} prepedido(s) capturados`,
        html: `${num(d.renglones)} renglones · ${num(d.piezas)} piezas<br>
               Se dieron de alta ${d.articulosCreados.length} modelos, ${d.coloresCreados.length} colores y ${d.tallasCreadas.length} tallas.
               ${(d.dibujos || []).length ? `<br>${d.dibujos.length} dibujo(s) aparte: ${d.dibujos.slice(0, 6).map((x) => x.codigo).join(", ")}${d.dibujos.length > 6 ? "..." : ""}` : ""}
               ${(d.clientesCreados || []).length ? `<br><br>Clientes nuevos dados de alta: <b>${d.clientesCreados.join(", ")}</b> (completa sus datos en el catálogo de clientes).` : ""}
               ${d.sinCliente.length ? `<br><br><b style="color:#b45309">Sin cliente no se capturan: ${d.sinCliente.length} renglón(es) sin CLIENTE.</b>` : ""}
               ${(d.yaImportados || []).length ? `<br><br><b style="color:#b45309">Se saltaron ${d.yaImportados.length} que ya estaban capturados: ${d.yaImportados.map((x) => `${x.prepedido} (pedido #${x.idPedido})`).join(", ")}</b>` : ""}`,
      });
      cerrar();
      if (onImportado) onImportado();
    } catch (error) {
      setGuardando(false);
      Swal.close();
      Swal.fire({ icon: "error", title: "No se pudo importar", text: error.response?.data?.message || error.message });
    }
  }

  return (
    <Modal size="xl" isOpen={abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>Subir prepedidos del cliente</ModalHeader>
      <ModalBody>
        <Row>
          <Col md={5}>
            <Label style={{ fontWeight: 600 }}>1. El layout</Label>
            <div>
              <Button size="sm" color="success" onClick={descargaLayout}>
                <i className="fas fa-download" /> Descargar layout
              </Button>
            </div>
            <small style={{ color: "#64748b" }}>
              Mándaselo al cliente: son las mismas columnas de su "BASE DE DATOS".
            </small>
          </Col>
          <Col md={4}>
            <Label style={{ fontWeight: 600 }}>2. Súbelo lleno</Label>
            {/* se limpia el valor para que, si corrigen el Excel y lo vuelven a elegir con el
                mismo nombre, se lea otra vez (antes el navegador no avisaba el cambio) */}
            <Input type="file" accept=".xlsx,.xls"
              onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; leeArchivo(f); }} />
            <small style={{ color: "#64748b" }}>
              {archivo || "La hoja PP. Si el archivo trae fotos, guárdalo sin ellas: pesa menos y sube más rápido."}
            </small>
          </Col>
          <Col md={3} className="text-right">
            {prepedidos.length ? (
              <div style={{ fontSize: 13, color: "#64748b" }}>
                <b style={{ color: "#15803d", fontSize: 15 }}>{marcados.length}</b> prepedido(s) marcados ·{" "}
                {num(modelos)} modelos · {num(renglones)} renglones · <b>{num(piezas)}</b> pzas
                {modelos ? (
                  <div style={{ color: "#b45309" }}>
                    Cada renglón da de alta su propio modelo (un dibujo = un artículo).
                    {dibujosExtra === 1 ? " 1 viene repetido en el archivo." : dibujosExtra > 1 ? ` ${dibujosExtra} vienen repetidos en el archivo.` : ""} Los códigos ocupados llevan sufijo (-2, -3...).
                  </div>
                ) : null}
                {avisos.length ? (
                  <div>
                    <Button size="sm" color="link" style={{ padding: 0 }} onClick={() => setVerAvisos(!verAvisos)}>
                      {avisos.length} avisos {verAvisos ? "▲" : "▼"}
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </Col>
        </Row>
        {verAvisos ? (
          <div style={{ maxHeight: 140, overflowY: "auto", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: 8, margin: "8px 0", fontSize: 12 }}>
            {avisos.slice(0, 200).map((a, i) => (
              <div key={i}>renglón {a.renglon}{a.modelo ? ` · ${a.modelo}` : ""}: {a.aviso}</div>
            ))}
          </div>
        ) : null}
        <br />
        {prepedidos.length ? (
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            <Table size="sm" bordered>
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th>Prepedido / OC</th><th>Cliente</th><th>Proveedor(es)</th>
                  <th className="text-right">Modelos</th><th className="text-right">Renglones</th><th className="text-right">Piezas</th>
                </tr>
              </thead>
              <tbody>
                {prepedidos.map((p) => {
                  const k = p.numero + p.cliente;
                  const celdas = p.articulos.reduce((x, a) => x + a.celdas.length, 0);
                  return (
                    <tr key={k}>
                      <td className="text-center">
                        <Input type="checkbox" style={{ position: "static", margin: 0 }}
                          checked={!!elegidos[k]} onChange={() => setElegidos((e) => ({ ...e, [k]: !e[k] }))} />
                      </td>
                      <td>
                        <b>{p.numero}</b>
                        {p.prepedidoArchivo && p.ordenCompraCliente ? (
                          <div style={{ fontSize: 11, color: "#64748b" }}>OC {p.ordenCompraCliente}</div>
                        ) : null}
                        {yaImportado(p) ? (
                          <div style={{ color: "#b45309", fontSize: 11 }}>
                            ya importado · pedido #{yaImportado(p).idPedido} del {yaImportado(p).fecha}
                          </div>
                        ) : null}
                      </td>
                      <td>{p.cliente || <span style={{ color: "#dc2626" }}>sin cliente</span>}</td>
                      <td style={{ fontSize: 12 }}>{p.proveedores.join(", ")}</td>
                      <td className="text-right">{p.articulos.length}</td>
                      <td className="text-right">{num(celdas)}</td>
                      <td className="text-right">{num(p.piezas)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        ) : (
          <p style={{ color: "#9ca3af" }}>Sube el archivo para ver qué prepedidos trae.</p>
        )}
      </ModalBody>
      <ModalFooter>
        <Label style={{ marginRight: "auto", color: "#64748b", fontSize: 12 }}>
          Los modelos, colores y tallas que no existan se dan de alta; lo que venga vacío queda en NA.
        </Label>
        <Button color="success" onClick={importa} disabled={guardando || !marcados.length}>
          {guardando ? "Capturando..." : `Capturar ${marcados.length || ""}`}
        </Button>
        <Button color="secondary" onClick={cerrar}>Cerrar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default ImportarPrepedidoModal;
